import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Search,
  ShieldCheck,
  BadgeCheck,
  Star,
  Sparkles,
  Clock,
  ArrowRight,
  Wrench,
  Paintbrush,
  Zap,
  Home as HomeIcon,
  Truck,
  Leaf,
  LogOut,
  User,
  ChevronDown,
  Hand,
  MapPin,
} from "lucide-react";
import { formatRupiah } from "../../components/ui";
import { useAuth } from "../../context/AuthContext";
import NotificationBell from "../../components/NotificationBell";
import { ProfileAvatar, profilePhotoSrc } from "../../components/ProfilePhoto";
import UsageTour, { TourReplayButton } from "../../components/UsageTour";
import BannerCarousel from "../../components/BannerCarousel";
import { api } from "../../lib/api";
import { cacheGet, cacheSet, cacheKey } from "../../lib/cache";

const CUSTOMER_TOUR = [
  {
    title: "Selamat datang di Cariinjasa! 👋",
    text: "Panduan 30 detik: cara pesan jasa, bayar aman, sampai komplain bila ada masalah. Bisa dilewati kapan saja.",
  },
  {
    selector: "customer-search",
    title: "1. Cari jasa",
    text: "Ketik kebutuhanmu (mis. 'servis AC'), atau telusuri kategori di bawah. Hasil bisa diurutkan termurah & terpopuler.",
    to: "/customer/services",
    toLabel: "Buka Cari Jasa",
  },
  {
    selector: "popular-service-0",
    title: "2. Pilih dari yang terlaris",
    text: "Ini ranking REAL berdasarkan pesanan terbanyak di daerahmu — lengkap dengan pekerja terbaik, rating, dan harga mulai.",
  },
  {
    selector: "quick-booking",
    title: "3. Pesan, bayar, konfirmasi di sini",
    text: "Alur pesanan: pilih pekerja → isi jadwal → BAYAR (dana ditahan aman) → pekerja kerja → periksa bukti foto → KONFIRMASI. Uang cair ke pekerja hanya setelah kamu konfirmasi. Belum tahu rusaknya apa? Pesan 'Cek & Diagnosa' dulu — tambahan biaya diajukan pekerja dan harus kamu setujui dulu.",
    to: "/customer/bookings",
    toLabel: "Lihat Pesananku",
  },
  {
    selector: "notif-bell",
    title: "4. Notifikasi real-time",
    text: "Pekerja terima/tolak, mulai, selesai, sampai refund — semuanya masuk ke lonceng ini.",
  },
  {
    title: "5. Ada masalah? Komplain langsung 🛡️",
    text: "Tanpa perlu CS: buka Pesananku → detail pesanan → 'Komplain ke CS'. Bisa juga batalkan (refund ikut aturan) atau beri rating setelah selesai. Dana 100% aman selama belum konfirmasi.",
    to: "/customer/bookings",
    toLabel: "Coba Lihat Pesanan",
  },
];

const CATEGORIES = [
  { name: "Kebersihan Rumah", slug: "kebersihan-rumah", desc: "Bersih-bersih & cuci", icon: Sparkles, color: "bg-blue-50 text-blue-600" },
  { name: "Servis AC", slug: "servis-ac", desc: "Pasang & perbaikan AC", icon: Wrench, color: "bg-cyan-50 text-cyan-600" },
  { name: "Tukang Ledeng", slug: "tukang-ledeng", desc: "Pipa & keran bocor", icon: Wrench, color: "bg-orange-50 text-orange-600" },
  { name: "Listrik", slug: "listrik", desc: "Instalasi & perbaikan", icon: Zap, color: "bg-yellow-50 text-yellow-600" },
  { name: "Cat & Renovasi", slug: "cat-renovasi", desc: "Pengecatan dinding", icon: Paintbrush, color: "bg-purple-50 text-purple-600" },
  { name: "Pindahan", slug: "pindahan", desc: "Bantuan pindah rumah", icon: Truck, color: "bg-green-50 text-green-600" },
  { name: "Taman & Kebun", slug: "taman-kebun", desc: "Perawatan taman", icon: Leaf, color: "bg-emerald-50 text-emerald-600" },
  { name: "Servis Elektronik", slug: "servis-elektronik", desc: "Perbaikan elektronik", icon: HomeIcon, color: "bg-rose-50 text-rose-600" },
  { name: "Pijat & Massage", slug: "pijat-massage", desc: "Pijat & relaksasi", icon: Hand, color: "bg-pink-50 text-pink-600" },
];

export default function CustomerHome() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);

  const displayName = user?.profile?.display_name || "Pelanggan";
  const initials = (displayName || "P")[0].toUpperCase();
  const userCity = user?.profile?.city || "";
  const [popular, setPopular] = useState(() => {
    const cached = cacheGet(cacheKey(user?.id, `popular:${userCity}`)).data;
    return normalizePopular(cached);
  });

  useEffect(() => {
    let alive = true;
    const key = cacheKey(user?.id, `popular:${userCity}`);
    (async () => {
      try {
        const q = userCity ? `?city=${encodeURIComponent(userCity)}&limit=5` : "?limit=5";
        const { data } = await api.get(`/api/services/popular${q}`);
        if (alive) {
          setPopular(normalizePopular(data));
          cacheSet(key, normalizePopular(data), 300000);
        }
      } catch {
        if (alive && !cacheGet(key).data) setPopular({ scope: "global", city: null, items: [] });
      }
    })();
    return () => { alive = false; };
  }, [userCity, user?.id]);

  const doLogout = async () => {
    setProfileOpen(false);
    await logout();
    navigate("/login");
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="animate-fade-up grid place-items-center py-8">
      <UsageTour tourKey="tour-customer-v1" steps={CUSTOMER_TOUR} />
      <div className="container space-y-6">
        {/* Greeting + Search */}
        <div className="relative z-20 rounded-3xl bg-brand-700 text-white p-6 md:p-8">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-brand-100">Halo,</p>
              <h1 className="text-2xl md:text-3xl">{displayName} 👋</h1>
            </div>
            {/* Profile dropdown */}
            <div className="flex items-center gap-1">
              <NotificationBell tone="dark" />
              <TourReplayButton tourKey="tour-customer-v1" />
              <div className="relative" ref={profileRef}>
              <button
                onClick={() => setProfileOpen(!profileOpen)}
                className="h-11 w-11 rounded-full overflow-hidden bg-white/15 grid place-items-center text-sm font-bold text-white hover:bg-white/25 transition"
                data-testid="customer-home-profile-btn"
              >
                <ProfileAvatar
                  userId={user?.id}
                  photoUrl={user?.profile?.profile_photo_url}
                  name={displayName}
                  className="h-11 w-11 rounded-full"
                  fallbackClassName="h-11 w-11 rounded-full bg-white/15 grid place-items-center text-sm font-bold text-white"
                />
              </button>
              {profileOpen && (
                <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-2xl shadow-lg border border-brand-100 py-2 animate-fade-up z-50">
                  <div className="px-4 py-2 border-b border-brand-50">
                    <p className="text-sm font-bold text-brand-800">{displayName}</p>
                    <p className="text-xs text-brand-400">Pelanggan</p>
                  </div>
                  <Link
                    to="/customer/profile"
                    onClick={() => setProfileOpen(false)}
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-brand-700 hover:bg-brand-50 transition"
                    data-testid="home-profile-link"
                  >
                    <User className="h-4 w-4" /> Profil Saya
                  </Link>
                  <button
                    onClick={doLogout}
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition w-full text-left"
                    data-testid="home-logout-btn"
                  >
                    <LogOut className="h-4 w-4" /> Keluar
                  </button>
                </div>
              )}
              </div>
            </div>
          </div>
          <div className="mt-5 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-brand-300" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full rounded-2xl bg-white px-4 py-3.5 pl-12 text-brand-800 placeholder:text-brand-300 focus:outline-none focus:ring-4 focus:ring-brand-200"
              placeholder="Cari layanan (mis. servis AC)"
              data-testid="customer-search"
            />
          </div>
          <Link
            to="/customer/services"
            className="mt-3 inline-flex items-center gap-1 text-sm text-brand-200 hover:text-white transition"
          >
            Lihat semua layanan <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        {/* Profile completion banner */}
        {(!user?.profile?.display_name || !user?.profile?.city) && (
          <Link to="/customer/profile" className="card p-4 border-amber-200 bg-amber-50 flex items-center gap-3 hover:border-amber-300 transition" data-testid="profile-completion-banner">
            <div className="h-10 w-10 rounded-xl bg-amber-100 grid place-items-center flex-shrink-0">
              <BadgeCheck className="h-5 w-5 text-amber-600" />
            </div>
            <div className="flex-1">
              <p className="font-bold text-brand-800">Lengkapi profil Anda</p>
              <p className="text-sm text-brand-500">
                {!user?.profile?.display_name && !user?.profile?.city
                  ? "Tambahkan nama tampilan dan kota untuk pengalaman yang lebih baik."
                  : !user?.profile?.display_name
                    ? "Tambahkan nama tampilan Anda."
                    : "Tambahkan kota Anda."}
              </p>
            </div>
            <ArrowRight className="h-5 w-5 text-amber-500 flex-shrink-0" />
          </Link>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-2 gap-3">
          <Link to="/customer/bookings" className="card p-4 flex items-center gap-3 hover:border-brand-200 transition" data-testid="quick-booking">
            <div className="h-11 w-11 rounded-xl bg-brand-50 grid place-items-center">
              <Clock className="h-6 w-6 text-brand-600" />
            </div>
            <div>
              <p className="font-bold text-brand-800">Pesanan Saya</p>
              <p className="text-sm text-brand-400">Lihat status pesanan</p>
            </div>
          </Link>
          <Link to="/customer/profile" className="card p-4 flex items-center gap-3 hover:border-brand-200 transition" data-testid="quick-profile">
            <div className="h-11 w-11 rounded-xl bg-success-50 grid place-items-center">
              <BadgeCheck className="h-6 w-6 text-success-500" />
            </div>
            <div>
              <p className="font-bold text-brand-800">Profil Saya</p>
              <p className="text-sm text-brand-400">Kelola akun</p>
            </div>
          </Link>
        </div>

        {/* Categories */}
        <div>
          <div className="mb-4">
            <h2 className="text-brand-800">Kategori Layanan</h2>
            <p className="text-brand-400 mt-1">Pilih kategori untuk memulai</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {CATEGORIES.map((c, i) => (
              <Link
                key={c.name}
                to={`/customer/services?category=${c.slug}`}
                className="card p-4 text-left hover:border-brand-200 transition group"
                data-testid={`category-card-${i}`}
              >
                <div className={`h-11 w-11 rounded-xl ${c.color} grid place-items-center`}>
                  <c.icon className="h-6 w-6" />
                </div>
                <p className="font-bold text-brand-800 mt-3">{c.name}</p>
                <p className="text-sm text-brand-400 mt-0.5">{c.desc}</p>
              </Link>
            ))}
          </div>
        </div>

        {/* Banner program (C1) */}
        <BannerCarousel audience="customer" />

        {/* Popular Services — ranking real dari booking (kota dulu, lalu global) */}
        <div>
          <div className="mb-4">
            <h2 className="text-brand-800">
              {popular?.scope === "city" && popular?.city
                ? `Paling Banyak Dipesan di ${popular.city}`
                : "Paling Banyak Dipesan"}
            </h2>
            <p className="text-brand-400 mt-1">
              {popular?.scope === "city" && popular?.city
                ? `Terlaris di daerahmu${popular.items?.some((i) => i.rank_scope === "global") ? " + nasional" : ""}`
                : "Terlaris di Cariinjasa"}
            </p>
          </div>
          {popular === null ? (
            <div className="card p-4 text-sm text-brand-400">Memuat rekomendasi...</div>
          ) : !popular?.items?.length ? (
            <div className="card p-4 text-sm text-brand-400">Belum ada data pesanan.</div>
          ) : (
            <div className="space-y-3">
              {popular.items.map((item, i) => {
                const s = item.service || {};
                const w = item.top_worker;
                // Di scope kota: tampilkan angka kota; tapi item fallback nasional
                // tampilkan angka nasionalnya (bukan 0).
                const isNationalFallback = popular.scope === "city" && item.rank_scope === "global";
                const orders = isNationalFallback ? item.bookings_total : (popular.scope === "city" ? item.bookings_city : item.bookings_total);
                return (
                  <Link
                    key={s.id || i}
                    to={`/customer/services/${s.slug || ""}`}
                    className="card p-4 hover:border-brand-200 transition block"
                    data-testid={`popular-service-${i}`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className="h-8 w-8 rounded-xl bg-brand-600 text-white grid place-items-center font-extrabold flex-shrink-0">
                          {i + 1}
                        </div>
                        <div className="min-w-0">
                          <p className="font-bold text-brand-800 truncate">{s.name || "Layanan"}</p>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            {w?.has_profile_photo ? (
                              <img
                                src={profilePhotoSrc(w.profile_photo_url)}
                                alt={w.display_name || "Pekerja"}
                                className="h-5 w-5 rounded-full object-cover"
                                loading="lazy"
                              />
                            ) : null}
                            <span className="text-sm text-brand-500">{w?.display_name || "Berbagai pekerja"}</span>
                            {item.average_rating ? (
                              <span className="flex items-center gap-1 text-sm text-brand-500">
                                <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" /> {item.average_rating}
                              </span>
                            ) : null}
                            {w?.tier && (
                              <span className="chip bg-brand-50 text-brand-600 text-xs">{w.tier}</span>
                            )}
                            {w?.city && (
                              <span className="flex items-center gap-1 text-xs text-brand-400">
                                <MapPin className="h-3 w-3" /> {w.city}
                                {w.city_match && <span className="text-success-600 font-semibold">• terdekat</span>}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-brand-400 mt-1">
                            {orders}x dipesan{isNationalFallback ? " di seluruh Indonesia" : popular.scope === "city" ? ` di ${popular.city}` : ""}
                          </p>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-lg font-extrabold text-brand-800">
                          {formatRupiah(w?.custom_price || s.base_price)}
                        </p>
                        <p className="text-xs text-brand-400">mulai dari</p>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>

        {/* Trust */}
        <div className="card p-6">
          <div className="mb-3">
            <h2 className="text-brand-800">Perlindungan Anda</h2>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <Protect icon={ShieldCheck} title="Pembayaran aman" desc="Dana ditahan sampai pekerjaan selesai." />
            <Protect icon={BadgeCheck} title="Pekerja terverifikasi" desc="Identitas & KYC diperiksa." />
            <Protect icon={Star} title="Garansi pekerjaan" desc="Masa perlindungan 7 hari." />
          </div>
        </div>
      </div>
    </div>
  );
}

function normalizePopular(d) {
  return d && typeof d === "object" && Array.isArray(d.items)
    ? { scope: d.scope === "city" ? "city" : "global", city: d.city || null, items: d.items }
    : { scope: "global", city: null, items: [] };
}

function Protect({ icon: Icon, title, desc }) {
  return (
    <div className="rounded-2xl bg-brand-50 p-4">
      <Icon className="h-6 w-6 text-success-500" />
      <p className="font-bold text-brand-800 mt-2">{title}</p>
      <p className="text-sm text-brand-500">{desc}</p>
    </div>
  );
}
